import React, { useState, useEffect, useRef } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { Routes, Route } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { store } from './store';
import SoccerField from './components/field/SoccerField';
import FormationSelector from './components/formation/FormationSelector';
import PlayerSearch, { PlayerSearchHandle } from './components/players/PlayerSearch';
import SubstituteList from './components/players/SubstituteList';
import TeamNameEditor from './components/TeamNameEditor';
import TeamExportImport from './components/TeamExportImport';
import TeamSaveStatus from './components/TeamSaveStatus';
import AuthCallback from './components/auth/AuthCallback';
import PlayerAIChat from './components/ai/PlayerAIChat';
import NotificationToast from './components/notifications/NotificationToast';
import PlayerInfoCard from './components/players/PlayerInfoCard';
import UserMenu from './components/auth/UserMenu';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RateLimitProvider } from './contexts/RateLimitContext';
import { RootState } from './store';
import { resetTeam } from './store/teamSlice';
import { Player, Position } from './types';
import { useTeamManagement } from './hooks/useTeamManagement';
import './App.css';

// Create a separate component for the main app content so it can use Redux hooks
const AppContent = () => {
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 900);
  const fieldOrientation = useSelector((state: RootState) => state.team.fieldOrientation);
  const playerSearchRef = useRef<PlayerSearchHandle>(null);
  const dispatch = useDispatch();
  
  // Authentication state
  const { user } = useAuth();
  const isAuthenticated = !!user;
  
  // Team management with Supabase
  const { 
    isLoading: isTeamLoading, 
    hasUnsavedChanges, 
    canSave,
    saveTeam,
    loadTeam 
  } = useTeamManagement();
  
  // Load team data when user authenticates (centralized to prevent duplicate notifications)
  useEffect(() => {
    if (isAuthenticated && user) {
      loadTeam();
    }
  }, [isAuthenticated, user?.id, user, loadTeam]);
  
  // Shared state for player info card (used by both field players and substitutes)
  const [selectedPlayer, setSelectedPlayer] = useState<{player: Player; position: Position} | null>(null);

  // Handle window resize to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle reset team action
  const handleResetTeam = () => {
    if (window.confirm("Are you sure you want to start a new team? This will clear all players, substitutes, and settings.")) {
      dispatch(resetTeam());
    }
  };

  // Shared handlers for player info card
  const handlePlayerClick = (player: Player, position: Position) => {
    setSelectedPlayer({ player, position });
  };

  const handleClosePlayerInfo = () => {
    setSelectedPlayer(null);
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-brand">
            <div className="header-logo"></div>
            <div className="header-title-section">
              <h1>Dream 11 Soccer Team Builder</h1>
              <div className="header-subtitle">Build Your Ultimate Squad</div>
            </div>
          </div>
          <div className="header-user-section">
            <div className="header-auth-container">
              <UserMenu />
              <div className="header-status-indicator"></div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="App-main">
        <div className="controls-panel">
          {/* Team Save Status - Shows save status and manual save button */}
          <TeamSaveStatus />
          
          <div className="primary-controls">
            {/* Desktop formation selector, hidden on mobile */}
            <div className="desktop-only-formation">
              <FormationSelector />
            </div>
            {/* This search component will be hidden on mobile */}
            {!isMobileView && <PlayerSearch ref={playerSearchRef} />}
          </div>
        </div>
        
        <div className="field-section">
          {/* Mobile formation selector and search, only visible on mobile */}
          {isMobileView && (
            <>
              <div className="mobile-formation-container">
                <FormationSelector />
              </div>
              <div className="mobile-search-container">
                <PlayerSearch ref={playerSearchRef} />
              </div>
            </>
          )}
          
          <div className={`field-and-subs-container ${fieldOrientation}`}>
            <div className="field-container">
              {/* Position team name above soccer field */}
              <div className="team-name-container">
                <TeamNameEditor />
              </div>
              <SoccerField 
                playerSearchRef={playerSearchRef} 
                onPlayerClick={handlePlayerClick}
              />
              
              {/* Unsaved Changes Prompt - appears below the field */}
              {isAuthenticated && hasUnsavedChanges && canSave && (
                <div className="unsaved-changes-prompt">
                  <div className="unsaved-changes-content">
                    <span className="unsaved-icon">⚠️</span>
                    <span className="unsaved-text">You have unsaved changes</span>
                    <button 
                      className="quick-save-btn"
                      onClick={() => saveTeam()}
                      disabled={!canSave}
                      title="Save your team changes to cloud"
                    >
                      {isTeamLoading ? '💾 Saving...' : '💾 Save'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Team controls moved inside field-container for better symmetry */}
              <div className={`field-controls-container ${isAuthenticated && hasUnsavedChanges && canSave ? 'with-unsaved-prompt' : ''}`}>
                <button 
                  className="reset-team-btn" 
                  onClick={handleResetTeam}
                  title="Clear all players and start a new team"
                >
                  Start New Team
                </button>
                
                <div className="export-import-wrapper">
                  <TeamExportImport />
                </div>
              </div>
            </div>
            <div className="substitute-container">
              <SubstituteList onPlayerClick={handlePlayerClick} />
            </div>
          </div>
        </div>
      </main>
      
      <footer className="App-footer">
        <p>Build your dream team with players from 1975 to 2025</p>
      </footer>
      
      {/* AI Chat Assistant */}
      <PlayerAIChat initialPosition="bottom-right" />
      
      <NotificationToast />
      
      {/* Shared Player Info Card */}
      {selectedPlayer && (
        <PlayerInfoCard 
          player={selectedPlayer.player} 
          position={selectedPlayer.position}
          onClose={handleClosePlayerInfo} 
        />
      )}
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <RateLimitProvider>
          <DndProvider backend={HTML5Backend}>
            <Analytics />
            <SpeedInsights />
            <Routes>
              <Route path="/callback" element={<AuthCallback />} />
              <Route path="/" element={<AppContent />} />
            </Routes>
          </DndProvider>
        </RateLimitProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
